// Снятие записи чёрного списка (тз 5.8.2, сценарий 90).
//
// Панель, а не модалка: снятие ничего не стирает. Запись остаётся на месте вместе с причиной,
// правовым основанием и всей историей, к ней лишь добавляются дата, автор и причина снятия.
// Модалка тут была бы обещанием необратимости, которой нет, — а окно необратимого действия у
// нас занято стиранием, и обесценивать его нельзя.
//
// Половина обращений «уберите его из чёрного списка» на деле про снятие: человек рассчитался,
// вопрос закрыт, к найму его допускают снова. Поэтому форма объясняет разницу до нажатия, а не
// после.

import type { FormEvent } from 'react';
import { useState } from 'react';

import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { Skeleton } from '@/shared/ui/screen-state';
import { TextArea } from '@/shared/ui/text-area';

import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { useBlacklistEntryQuery, useLiftEntryMutation } from '../queries';

import { entryTitle } from '../views/BlacklistList';

/** Снятие записи: право администратора. */
const MANAGE_PERMISSION = 'hiring.blacklist.manage';

/** Столько знаков причины требует сервер. Меньше — отказ, и знать это надо до отправки. */
const MIN_REASON = 3;

export function LiftEntryPanel({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const allowed = useCan(MANAGE_PERMISSION);
  const entry = useBlacklistEntryQuery(entryId);
  const mutation = useLiftEntryMutation(entryId);

  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [gateNote, setGateNote] = useState<string | null>(null);

  const ужеСнята = entry.data?.status === 'lifted';

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Снимать записи чёрного списка может администратор системы. Попросите его — заполненная причина никуда не денется.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Запись уже снимается, ждём ответ сервера. Второй раз не отправляем.');
      return;
    }
    if (ужеСнята) {
      setGateNote('Эта запись уже снята: снимать её второй раз нечего.');
      return;
    }
    const причина = reason.trim();
    if (причина.length < MIN_REASON) {
      setReasonError(
        причина === ''
          ? 'Причина обязательна: через полгода никто не вспомнит, почему человека вернули к найму.'
          : 'Причина короче трёх знаков не сохранится. Напишите фразой, а не одной буквой.',
      );
      return;
    }
    setReasonError(undefined);
    setGateNote(null);
    mutation.mutate(причина, { onSuccess: () => onClose() });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {/* Скелет показывается, только пока запрос идёт. Условие «данных нет» держало бы его
          вечно: запись может не приехать вовсе — например, панель открыли по старой ссылке на
          уже уничтоженную. Вечный скелет читается как «сейчас догрузится» и не догружается. */}
      {entry.data === undefined && entry.isPending ? (
        <Skeleton className='h-4 w-56' />
      ) : entry.data === undefined ? null : (
        <p className='flex flex-wrap items-center gap-2 text-paragraph-xs text-text-sub-600'>
          <span>{entryTitle(entry.data)}</span>
          <Badge.Root
            variant='lighter'
            color={entry.data.status === 'active' ? 'red' : 'gray'}
            size='medium'
          >
            {entry.data.status_title}
          </Badge.Root>
        </p>
      )}

      {!allowed && (
        <NoPermissionNote
          что='снимать записи чёрного списка'
          кто='Это делает администратор системы.'
        />
      )}

      <p className='text-paragraph-sm text-text-sub-600'>
        Снятие — не удаление. Запись останется в списке вместе с причиной и правовым основанием, к
        ней добавятся дата снятия, автор и то, что вы сейчас напишете. Изменится одно: сверка
        перестанет находить этого человека, и он снова сможет наниматься.
      </p>

      <TextArea
        id='lift-entry-reason'
        label='Причина снятия'
        required
        rows={3}
        value={reason}
        onChange={setReason}
        placeholder='Например: долг погашен, претензий у заказчика нет'
        error={reasonError}
        hint='Останется в карточке записи навсегда и будет видна рядом с причиной внесения.'
      />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && <GateNote>{gateNote}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Снимаем…' : 'Снять запись'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
